
import { Table, Input, Row, Col, Button, Space, Modal } from 'antd';
import type { TableProps } from 'antd';
import InvitationMember from '../InvitationMember'
import InvitationLink from '@/components/InvitationLink'
import { IconFont } from '@/components/IconFont'
import classnames from 'classnames'
import EditMemberModal from '../EditMember'
import { Member } from '@/services/member/member.model'
import { useMemo, useState } from 'react'
import { textIncludes } from '@/utils/utils'
import { MemberName } from '@/components/MemberName'
import { DisconnectOutlined, ExclamationCircleOutlined } from '@ant-design/icons'
import { deleteProjectMember, leaveProject } from '@/services/member/member'
import { PROJECT_ACCOUNT_TYPES } from '@/utils/constants'
import { Project } from '@/services/project/project.model'
import { ColumnFilterItem } from 'antd/es/table/interface'
import { useSelector } from 'react-redux'
import { selectUser } from '@/store/user/user.slice'

const { Column } = Table;

export const accountTypeFilters: ColumnFilterItem[] = Object.keys(PROJECT_ACCOUNT_TYPES).map((key) => ({
  text: (PROJECT_ACCOUNT_TYPES[key] as string).toUpperCase(),
  value: key
}))

interface ProjectMemberTableProps<T> extends TableProps<T> {
  members: Member.ProjectMember[]
  reload: () => void;
  loading: boolean
  project: Project.Info
  me?: Member.ProjectMember
}

const ProjectMemberTable = <T extends object>({ members, reload, loading, project, me }: ProjectMemberTableProps<T>) => {
  const [filterValue, setFilterValue] = useState('')

  const filterMembers = useMemo(() => {
    return members.filter((member) => textIncludes(member.fullName, filterValue))
  }, [members, filterValue])

  const isOwner = (user) => {
    return user?.accountType === PROJECT_ACCOUNT_TYPES.OWNER.toUpperCase()
  }

  const isAdmin = (user) => {
    return user?.accountType === PROJECT_ACCOUNT_TYPES.ADMINISTRATOR.toUpperCase()
  }

  const isMe = (user) => {
    return user?.email === me?.email
  }

  const userIsAdmin = isOwner(me) || isAdmin(me)

  return <div className={classnames('p-2')}>
      <Row justify={'end'}>
        <Col>
          <Input
            value={filterValue}
            placeholder='Search'
            className={'mr-1'}
            style={{ width: '200px' }}
            prefix={<IconFont type={'icon-search'} />}
            onChange={(event) => {
              const value = event.target.value
              setFilterValue(value)
            }}
          />

          <InvitationMember
            trigger={
              <Button type={'primary'} icon={<IconFont type='icon-add' />}>
                Invitation
              </Button>
            }
            onSuccess={(link) => {
              InvitationLink.show({ link })
              reload()
            }}
          />
        </Col>
      </Row>

      <div className={classnames('mt-2')}>
        <Table
          bordered
          loading={loading}
          size='small'
          dataSource={filterMembers}
          rowKey={(record) => record.memberId}
          title={() => `${members.length} people in the ${project.name}`}
        >
          <Column
            title='Name'
            dataIndex='name'
            align={'center'}
            render={(text, record: Member.Info) => <MemberName member={record} />}
          />

          <Column
            title='Account type'
            dataIndex='accountType'
            key='accountType'
            align={'center'}
            filters={accountTypeFilters}
            onFilter={(value, record: Member.Info) => record.accountType === value}
            render={(value) => (PROJECT_ACCOUNT_TYPES[value] as string).toUpperCase()}
          />

          <Column
            title='Operation'
            dataIndex='title'
            align={'center'}
            render={(_, record: Member.ProjectMember) => (
              <Space>
                {(userIsAdmin || isMe(me)) && <IconFont
                  type='icon-filter'
                  onClick={() => {
                    EditMemberModal.show({
                      title: 'Edit Member',
                      projectId: project.id,
                      member: record,
                      onSuccess: () => {
                        reload()
                      }
                    })
                  }}
                />}

                {!isOwner(record) && isMe(record) && <DisconnectOutlined title='Leave to project'
                  onClick={() => {
                    Modal.confirm({
                      icon: <ExclamationCircleOutlined />,
                      content: 'Are you sure to leave?',
                      onOk: async () => {
                        await leaveProject(project.id)
                        reload()
                      }
                    })
                  }}
                
                />}
                {!isOwner(record) && !isMe(record) && userIsAdmin && <IconFont
                  type='icon-shanchu'
                  onClick={() => {
                    Modal.confirm({
                      icon: <ExclamationCircleOutlined />,
                      content: 'Are you sure to delete?',
                      onOk: async () => {
                        await deleteProjectMember(project.id, record.memberId)
                        reload()
                      }
                    })
                  }}
                />}
              </Space>
            )}
          />
        </Table>
      </div>
    </div>
};

export default ProjectMemberTable;
